/**
 * EmptyState — the original W AI welcome screen and explicit arcade preview.
 */
import { VIDYA_PREVIEW_GAMES } from '../../constants/dubyaToken';
import type { AllowanceState } from '../../types';
import { DubyaDropDemo } from '../arcade/DubyaDropDemo';
import styles from '../../styles/components/EmptyState.module.css';

const CAMPAIGN_PATTERN = /^dubya_video_(?:0[1-9]|1[0-9]|20)$/;
const SHARED_CAMPAIGNS = new Set(['shared_answer', 'shared_build', 'shared_game']);
const COMMUNITY_CAMPAIGNS = new Set(['community_reply_01']);

export interface CampaignArrival {
  sourceLabel: 'A FRIEND' | 'TIKTOK' | 'X';
  campaign: string;
}

export function getCampaignArrival(search: string): CampaignArrival | null {
  const params = new URLSearchParams(search);
  if (
    params.getAll('utm_source').length !== 1 ||
    params.getAll('utm_campaign').length !== 1 ||
    params.getAll('utm_medium').length > 1
  ) {
    return null;
  }
  const source = params.get('utm_source');
  const medium = params.get('utm_medium');
  const campaign = params.get('utm_campaign');

  if (!campaign) return null;

  if (source === 'x' && medium === null && campaign === 'trust') {
    return { sourceLabel: 'X', campaign };
  }

  if (params.getAll('utm_medium').length !== 1) return null;

  if (medium === 'organic' && CAMPAIGN_PATTERN.test(campaign)) {
    if (source === 'x') return { sourceLabel: 'X', campaign };
    if (source === 'tiktok') return { sourceLabel: 'TIKTOK', campaign };
  }
  if (source === 'x' && medium === 'organic' && SHARED_CAMPAIGNS.has(campaign)) {
    return { sourceLabel: 'X', campaign };
  }
  if (source === 'x' && medium === 'organic' && COMMUNITY_CAMPAIGNS.has(campaign)) {
    return { sourceLabel: 'X', campaign };
  }
  if (source === 'direct' && medium === 'referral' && SHARED_CAMPAIGNS.has(campaign)) {
    return { sourceLabel: 'A FRIEND', campaign };
  }
  if (source === 'x' && medium === 'profile' && campaign === 'dubya_profile') {
    return { sourceLabel: 'X', campaign };
  }
  return null;
}

export function shouldShowVidyaPreview(search: string): boolean {
  const params = new URLSearchParams(search);
  return params.getAll('view').length === 1 && params.get('view') === 'vidya';
}

export function shouldFeatureDubyaDrop(search: string): boolean {
  const params = new URLSearchParams(search);
  return (
    params.getAll('view').length === 1 &&
    params.get('view') === 'vidya' &&
    params.getAll('demo').length === 1 &&
    params.get('demo') === 'dubya-drop'
  );
}

export function DailyLimitNotice({ allowance }: { allowance?: AllowanceState | null }) {
  if (!allowance || allowance.remaining > 0) return null;

  return (
    <aside className={styles.upgrade} aria-label="Daily message limit">
      <div>
        <span>DAILY LIMIT REACHED</span>
        <strong>Your free messages will renew automatically.</strong>
        <p>You have used {allowance.limit} messages in the last 24 hours. Please try again when a message renews.</p>
      </div>
    </aside>
  );
}

function VidyaPreview({
  campaignArrival,
  featureDubyaDrop,
}: {
  campaignArrival: CampaignArrival | null;
  featureDubyaDrop: boolean;
}) {
  const friendGameArrival =
    featureDubyaDrop &&
    campaignArrival?.sourceLabel === 'A FRIEND' &&
    campaignArrival.campaign === 'shared_game';

  return (
    <div className={styles.vidyaExperience}>
      {campaignArrival && (
        <aside className={styles.campaignArrival} aria-label="Campaign arrival">
          <span>WELCOME FROM {campaignArrival.sourceLabel}</span>
          <strong>{friendGameArrival ? 'FRIEND CHALLENGE' : 'PLAY FREE · NO WALLET'}</strong>
          <p>
            {friendGameArrival
              ? 'A 15-second score challenge is ready below. No payment or token reward.'
              : 'Try the arcade demo instantly. No account or wallet is required.'}
          </p>
        </aside>
      )}
      {featureDubyaDrop && (
        <section className={`${styles.vidyaPreview} ${styles.featuredDemo}`} aria-labelledby="featured-dubya-drop-title">
          <span className={styles.vidyaPreviewEyebrow}>PLAY INSTANTLY · NO WALLET</span>
          <h4 id="featured-dubya-drop-title">INSERT Drop · free 15-second demo</h4>
          <DubyaDropDemo />
          <p>Original browser mini-game. Free play requires no payment and offers no token reward.</p>
        </section>
      )}
      <section className={styles.vidyaPreview} aria-labelledby="vidya-preview-title">
        <span className={styles.vidyaPreviewEyebrow}>VIDYA CATALOG</span>
        <h4 id="vidya-preview-title">
          {featureDubyaDrop
            ? 'Preview two source-backed homebrew games'
            : 'Play one free demo, preview two homebrew games'}
        </h4>
        {!featureDubyaDrop && <DubyaDropDemo />}
        <div className={styles.vidyaPreviewGrid}>
          {VIDYA_PREVIEW_GAMES.map((game) => (
            <article key={game.title} className={styles.vidyaPreviewCard}>
              <strong>{game.title} v{game.version}</strong>
              <span>{game.platform}</span>
              <span>{game.access} · {game.license}</span>
              <div>
                <a href={game.sourceUrl} target="_blank" rel="noopener noreferrer">SOURCE</a>
                <a href={game.releaseUrl} target="_blank" rel="noopener noreferrer">RELEASE</a>
              </div>
            </article>
          ))}
        </div>
        <p>Paid access is granted only after server-verified payment.</p>
      </section>
    </div>
  );
}

export function EmptyState() {
  const campaignArrival =
    typeof window === 'undefined' ? null : getCampaignArrival(window.location.search);
  const showVidyaPreview =
    typeof window === 'undefined' ? false : shouldShowVidyaPreview(window.location.search);
  const featureDubyaDrop =
    typeof window === 'undefined' ? false : shouldFeatureDubyaDrop(window.location.search);

  if (showVidyaPreview) {
    return <VidyaPreview campaignArrival={campaignArrival} featureDubyaDrop={featureDubyaDrop} />;
  }

  return (
    <div className={styles.container}>
      <img className={styles.logo} src="/w-ai-mark.svg" width="48" height="48" alt="" />
      <h2 className={styles.title}>W AI</h2>
      <p className={styles.subtitle}>
        Start a conversation by typing a message below. Free to use, no account needed.
        Up to 100 messages per 24 hours.
      </p>
    </div>
  );
}

export default EmptyState;
